import axios from 'axios'
import { assetBase } from '../../main'

function dataUrlToFile (dataUrl, filename = 'image.jpg') {
  const arr = dataUrl.split(',')
  const mime = (arr[0].match(/:(.*?);/) || [])[1] || 'image/jpeg'
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) u8arr[n] = bstr.charCodeAt(n)
  return new File([u8arr], filename, { type: mime })
}

function formatUpdatedAgo (dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return ''
  const now = new Date()
  const diffMs = now - d
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffWeeks < 4) return `${diffWeeks}w ago`
  if (diffMonths < 12) return `${diffMonths}mo ago`
  return `${Math.floor(diffDays / 365)}y ago`
}

function normalizeMaterial (m) {
  const gallery = Array.isArray(m.gallery) ? m.gallery : []
  const materialCategories = Array.isArray(m.material_categories) ? m.material_categories : (Array.isArray(m.materialCategories) ? m.materialCategories : [])
  const toImgUrl = (path) => {
    if (!path || typeof path !== 'string') return ''
    if (path.startsWith('http')) return path
    if (path.startsWith('data:')) return path
    if (path.startsWith(assetBase)) return path
    return assetBase + (path.startsWith('/') ? path : '/' + path)
  }
  const squareTileRaw = m.square_tile || m.squareTile || ''
  const squareTile = toImgUrl(squareTileRaw) || squareTileRaw
  const heroBannerRaw = m.hero_banner || m.heroBanner || ''
  const heroBanner = toImgUrl(heroBannerRaw) || heroBannerRaw
  const galleryWithUrls = gallery.map((img) => {
    const raw = img.url || img.image || (typeof img === 'string' ? img : '')
    const url = typeof raw === 'string' ? toImgUrl(raw) || raw : raw
    return { url: url || (img.url || img.image || img) }
  })
  const displayImgUrl = squareTile || squareTileRaw
  return {
    id: m.id,
    name: m.display_name || m.name || '',
    displayName: m.display_name || m.name || '',
    displayNameEn: m.display_name_en || m.displayNameEn || '',
    urlSlug: m.url_slug || m.urlSlug || '',
    urlSlugEn: m.url_slug_en || m.urlSlugEn || '',
    narrative: m.narrative || '',
    narrativeEn: m.narrative_en || m.narrativeEn || '',
    videoEmbedUrl: m.video_embed_url || m.videoEmbedUrl || '',
    squareTile: squareTile || squareTileRaw,
    heroBanner: heroBanner || heroBannerRaw,
    gallery: galleryWithUrls,
    materialCategories: Array.isArray(materialCategories) ? materialCategories : [],
    tags: typeof m.tags === 'string' ? m.tags : (Array.isArray(m.tags) ? m.tags.join(', ') : ''),
    tag: m.tags || m.tag || '',
    tagColor: m.tag_color || m.tagColor || '',
    tagClass: m.tag_color || m.tagClass || 'gray',
    active: m.active !== undefined ? !!m.active : true,
    sectionOrder: Array.isArray(m.section_order) ? m.section_order : (Array.isArray(m.sectionOrder) ? m.sectionOrder : ['general', 'galerija', 'youtube']),
    lastEdited: m.last_edited || m.lastEdited || m.updated_at || '',
    itemCount: gallery.length,
    updatedAgo: formatUpdatedAgo(m.updated_at || m.last_edited),
    imageGradient: displayImgUrl ? `url(${displayImgUrl})` : 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)'
  }
}

const state = {
  materials: []
}

const getters = {
  materials: (state) => state.materials,
  materialById: (state) => (id) => state.materials.find((m) => String(m.id) === String(id))
}

const mutations = {
  setMaterials (state, list) {
    state.materials = Array.isArray(list) ? list.map(normalizeMaterial) : []
  },

  addMaterial (state, material) {
    state.materials.push(normalizeMaterial(material))
  },

  updateMaterial (state, payload) {
    const index = state.materials.findIndex((m) => String(m.id) === String(payload.id))
    if (index !== -1) {
      state.materials[index] = normalizeMaterial({ ...state.materials[index], ...payload })
    } else {
      state.materials.push(normalizeMaterial(payload))
    }
  },

  removeMaterial (state, id) {
    const index = state.materials.findIndex((m) => String(m.id) === String(id))
    if (index !== -1) state.materials.splice(index, 1)
  }
}

function toStoredPath (url) {
  if (!url || typeof url !== 'string') return url
  if (url.startsWith('data:') || url.startsWith('blob:')) return url
  let path = url
  if (/^https?:\/\//.test(path)) {
    try {
      path = new URL(path).pathname
    } catch (_) {
      return url
    }
  }
  if (assetBase && !/^https?:\/\//.test(assetBase) && path.startsWith(assetBase)) {
    path = path.slice(assetBase.length)
  }
  const marker = path.indexOf('/uploads/')
  if (marker > 0) path = path.slice(marker)
  return path.startsWith('/') ? path : '/' + path
}

function buildMaterialFormData (payload) {
  const form = new FormData()
  const str = (v) => (v == null ? '' : String(v))
  form.append('display_name', str(payload.displayName || payload.name))
  form.append('display_name_en', str(payload.displayNameEn))
  form.append('url_slug', str(payload.urlSlug))
  form.append('url_slug_en', str(payload.urlSlugEn))
  form.append('narrative', str(payload.narrative))
  form.append('narrative_en', str(payload.narrativeEn))
  form.append('video_embed_url', str(payload.videoEmbedUrl))
  form.append('tags', Array.isArray(payload.tags) ? payload.tags.join(', ') : str(payload.tags))
  form.append('tag_color', str(payload.tagColor))
  form.append('active', payload.active !== false ? '1' : '0')
  if (payload.materialCategories && payload.materialCategories.length) {
    form.append('material_categories', JSON.stringify(payload.materialCategories))
  } else {
    form.append('material_categories', JSON.stringify([]))
  }
  if (payload.squareTile && payload.squareTile instanceof File) {
    form.append('square_tile', payload.squareTile)
  } else if (payload.squareTile && typeof payload.squareTile === 'string' && payload.squareTile.startsWith('data:')) {
    try {
      form.append('square_tile', dataUrlToFile(payload.squareTile, 'square_tile.jpg'))
    } catch (_) {}
  } else if (payload.squareTile && typeof payload.squareTile === 'string') {
    form.append('square_tile', toStoredPath(payload.squareTile))
  }
  if (payload.heroBanner && payload.heroBanner instanceof File) {
    form.append('hero_banner', payload.heroBanner)
  } else if (payload.heroBanner && typeof payload.heroBanner === 'string' && payload.heroBanner.startsWith('data:')) {
    try {
      form.append('hero_banner', dataUrlToFile(payload.heroBanner, 'hero_banner.jpg'))
    } catch (_) {}
  } else if (payload.heroBanner && typeof payload.heroBanner === 'string') {
    form.append('hero_banner', toStoredPath(payload.heroBanner))
  }
  const gallery = payload.gallery || []
  const existingUrls = []
  const newFiles = []
  gallery.forEach((img, idx) => {
    const url = img.url || img.image
    if (url instanceof File) {
      newFiles.push(url)
    } else if (typeof url === 'string' && url.startsWith('data:')) {
      try {
        newFiles.push(dataUrlToFile(url, `gallery-${idx}.jpg`))
      } catch (_) {}
    } else if (url && typeof url === 'string' && !url.startsWith('blob:') && !url.startsWith('data:')) {
      existingUrls.push({ url: toStoredPath(url) })
    }
  })
  if (existingUrls.length) form.append('old_gallery', JSON.stringify(existingUrls))
  newFiles.forEach((f) => form.append('gallery[]', f))
  const sectionOrder = Array.isArray(payload.sectionOrder) ? payload.sectionOrder : ['general', 'galerija', 'youtube']
  form.append('section_order', JSON.stringify(sectionOrder))
  return form
}

const actions = {
  async load ({ commit }) {
    try {
      const { data } = await axios.get('/api/materials')
      const list = (data && data.data) ? data.data : []
      commit('setMaterials', list)
    } catch (e) {
      return Promise.reject(e)
    }
  },

  async fetchMaterial ({ commit }, id) {
    const { data } = await axios.get(`/api/material/${id}`)
    const m = data.data
    commit('updateMaterial', { id: m.id, ...m })
    return normalizeMaterial(m)
  },

  async createMaterial ({ commit }, payload) {
    const form = buildMaterialFormData(payload)
    const { data } = await axios.post('/api/material', form)
    const material = data.data
    commit('addMaterial', material)
    return normalizeMaterial(material)
  },

  async updateMaterial ({ commit }, payload) {
    const id = payload.id
    const form = buildMaterialFormData(payload)
    const { data } = await axios.post(`/api/material/${id}`, form)
    const material = data.data
    commit('updateMaterial', { id, ...material })
    return normalizeMaterial(material)
  },

  async deleteMaterial ({ commit }, id) {
    await axios.delete(`/api/material/${id}`)
    commit('removeMaterial', id)
  }
}

export default {
  state,
  getters,
  mutations,
  actions,
  namespaced: true
}
